import React, { useState } from 'react';
import { format } from 'date-fns';
import DOMPurify from 'dompurify';
import ptBR from 'date-fns/locale/pt-BR';
import {
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  UncontrolledTooltip
} from 'reactstrap';
import { FaInfoCircle, FaRegEnvelope } from 'react-icons/fa';
import { useProtocolAttachments } from '../../Attachments/useProtocolAttachments';
import CommentsForm from '../CommentsForm';
import { CommentReplyButton } from '../CommetReplyButton';
import { CommentForwardButton } from '../CommentForwardButton';
import { RepliedCommentBox } from './RepliedCommentBox';
import { MarkAsReadUnreadButton } from './MarkAsReadUnreadButton';
import { ForwardedCommentBox } from './ForwardedCommentBox';
import { useImageUrl } from 'hooks/useImageSrc';

import { CommentWrapper, CommentHeader, CommentBody } from './styled';

const CommentInfoBadge = ({
  commentAuthor,
  email,
  cc,
  commentedAt,
  commentId
}) => {
  const tooltipId = `comment-info-${commentId}`;
  const ccList = cc ? (Array.isArray(cc) ? cc.join(', ') : cc) : '';
  const infoText = commentAuthor
    ? `de: ${commentAuthor}\npara: ${email}\n emails em cópia: ${ccList}\ndata: ${commentedAt}`
    : '';
  return (
    <>
      <span
        className="badge badge-pill"
        id={tooltipId}
        style={{
          fontSize: '10px',
          color: '#2643e9',
          cursor: 'pointer'
        }}
        title={infoText}
      >
        <FaInfoCircle size={12} />
      </span>
      {infoText && (
        <UncontrolledTooltip placement="top" target={tooltipId}>
          {infoText.split('\n').map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </UncontrolledTooltip>
      )}
    </>
  );
};

const CommentDiv = ({ comment }) => {
  const meyHtmlEmail = `<div>${comment}</div>`;

  return (
    <div
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(meyHtmlEmail ?? '')
      }}
    />
  );
};

export default function CommentContainer({
  id,
  comment,
  onUpdate,
  onDelete,
  email,
  scrollToCommentForm,
  onReplyComment,
  parentComment = null,
  isSelected,
  onSelect,
  onForwardComment = () => {},
  forwardedItems = [],
  protocolId,
  protocol,
  onRefresh
}) {
  const { buildImageUrl } = useImageUrl();
  const baseURL = buildImageUrl();

  const signaturePath = comment.attachments.find((att) =>
    /signature-\d+/.test(att.name)
  );

  const [editMode, setEditMode] = useState(false);
  const {
    getFileURL,
    getFileExtensionFromName,
    getIconFromFileExtension,
    getFileNameFormatted
  } = useProtocolAttachments();
  const isCommentFromEmail = comment.channel === 'email';
  const commentAuthor = isCommentFromEmail ? comment.from : comment.user_id;
  const commentedAt = format(
    new Date(comment.creation_date),
    `dd 'de' LLLL 'de' yyyy 'às' HH:mm`,
    {
      locale: ptBR
    }
  );
  const isPublicComment = !comment.is_private;
  const isUnread = !comment?.is_read;

  const handleEditMode = () => {
    setEditMode((prev) => !prev);
  };

  const filterEmailCopy =
    comment?.destination && comment?.destination.length > 0
      ? comment?.destination
          .map(function (value) {
            return value.from;
          })
          .join(',\n')
      : '';

  return editMode ? (
    <CommentsForm
      comment={comment}
      onSubmit={onUpdate}
      onCancel={handleEditMode}
      protocol={protocol}
    />
  ) : (
    <CommentWrapper id={id} $isUnread={isUnread}>
      <CommentHeader $isUnread={isUnread}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <strong
            style={{
              color: '#525f7f'
            }}
          >
            {commentAuthor}
          </strong>
          {' comentou em '}
          <time>
            {commentedAt}
            {comment.scheduled_date && (
              <>
                {' (agendado para '}
                {format(
                  new Date(comment.scheduled_date),
                  `dd 'de' LLLL 'de' yyyy 'às' HH:mm`,
                  { locale: ptBR }
                )}
                {')'}
              </>
            )}
          </time>
          {isCommentFromEmail ? (
            <>
              {' via email '}
              <FaRegEnvelope size={16} />
            </>
          ) : null}
          <CommentInfoBadge
            commentAuthor={commentAuthor}
            email={
              comment.forwards && comment.forwards.length > 0
                ? comment.forwards[0].recipients.join(', ')
                : comment.to?.join(', ') || ''
            }
            cc={comment.cc}
            commentedAt={commentedAt}
            commentId={comment.id}
          />
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyItems: 'space-between'
          }}
        >
          <CommentReplyButton
            onClick={scrollToCommentForm}
            comment={comment}
            onReplyComment={onReplyComment}
          />

          <CommentForwardButton
            comment={comment}
            onClick={(isChecked) => {
              scrollToCommentForm();
              onForwardComment(isChecked);
            }}
            isSelected={isSelected}
            onSelect={onSelect}
          />

          <span>
            {!comment.is_private ? (
              <span
                className="badge badge-pill badge-primary"
                style={{
                  fontSize: '10px'
                }}
                title={email ? `${email}\n${filterEmailCopy}` : ''}
              >
                <i className="fas fa-users"></i>
              </span>
            ) : (
              [
                <span
                  key="private-comment-bagde"
                  className="badge badge-pill badge-primary"
                  style={{
                    fontSize: '10px'
                  }}
                  title={email ? `${email}\n${filterEmailCopy}` : ''}
                >
                  <i className="fas fa-users"></i>
                </span>
              ]
            )}
          </span>

          <span>
            {comment.is_private ? (
              <span
                className="badge badge-pill badge-primary"
                style={{
                  fontSize: '10px'
                }}
                title="Um comentário privado só pode ser visualizado pela plataforma do FlexUC"
              >
                Comentário Privado
              </span>
            ) : (
              <span
                className="badge badge-pill badge-secondary"
                style={{
                  fontSize: '10px'
                }}
                title="Um comentário público pode ser visualizado pela plataforma do FlexUC e pelo cliente onde será enviado um email para o cliente com o comentário"
              >
                Comentário Público
              </span>
            )}
          </span>

          <MarkAsReadUnreadButton
            isRead={!!comment?.is_read}
            protocolId={protocolId}
            commentId={comment?.id}
            onRefresh={onRefresh}
          />

          <UncontrolledDropdown>
            <DropdownToggle
              data-toggle="dropdown"
              tag="button"
              className="border-0 bg-transparent "
              style={{
                color: '#8898aa'
              }}
            >
              <i className="fas fa-ellipsis-h"></i>
            </DropdownToggle>
            <DropdownMenu right>
              <div id={`edit-tooltip-${comment.id}`}>
                <DropdownItem
                  disabled={
                    isCommentFromEmail ||
                    isPublicComment ||
                    comment.user_id !== localStorage.getItem('login')
                  }
                  onClick={handleEditMode}
                >
                  Editar
                </DropdownItem>
                <DropdownItem
                  disabled={
                    isCommentFromEmail ||
                    isPublicComment ||
                    comment.user_id !== localStorage.getItem('login')
                  }
                  onClick={onDelete}
                >
                  Deletar
                </DropdownItem>
              </div>
              {(isCommentFromEmail ||
                isPublicComment ||
                comment.user_id !== localStorage.getItem('login')) && (
                <UncontrolledTooltip
                  placement="top"
                  target={`edit-tooltip-${comment.id}`}
                >
                  {isCommentFromEmail || isPublicComment
                    ? 'Não é possível editar e excluir um comentário público'
                    : 'Você só pode editar ou excluir seus próprios comentários'}
                </UncontrolledTooltip>
              )}
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      </CommentHeader>
      <CommentBody>
        <div
          style={{
            whiteSpace: 'pre-wrap'
          }}
        >
          <CommentDiv comment={comment.description} />
          {signaturePath && (
            <img
              src={signaturePath.path.replace(
                /protocol-attachments/g,
                `${baseURL}/protocol-attachments`
              )}
              width={150}
              height={150}
            />
          )}
        </div>

        <RepliedCommentBox parentComment={parentComment} />
        {comment.forwards && comment.forwards.length > 0 && (
          <ForwardedCommentBox
            forwards={comment.forwards}
            forwardItems={forwardedItems}
          />
        )}
        {comment.attachments.length ? (
          <>
            <br />
            <div className="border-top py-2">
              <strong>{`${comment.attachments.length} ${
                comment.attachments.length > 1 ? 'Anexos' : 'Anexo'
              }`}</strong>
            </div>
            <div className="d-flex flex-column align-items-start">
              {comment.attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  title={attachment.name}
                  className="attachment-name-container p-2"
                >
                  <img
                    src={getIconFromFileExtension(
                      getFileExtensionFromName(attachment.name)
                    )}
                    width="24px"
                    height="24px"
                    alt={attachment.name}
                    className="d-inline-block mr-2"
                  />
                  <a
                    href={getFileURL(attachment.path)}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={true}
                  >
                    {getFileNameFormatted(attachment.name)}
                  </a>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </CommentBody>
    </CommentWrapper>
  );
}
